import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, MoveUpRightIcon } from 'lucide-react';

import {
  EditorHeader,
  EditorHeaderContainer,
  EditorHeaderContent,
  EditorHeaderInfo,
  EditorHeaderTopNav,
  EditorHeaderCoverPhoto,
  EditorHeaderTitle,
  EditorHeaderCaption,
  EditorHeaderTabNav
} from '@epoch-components/EditorHeader';
import { Badge } from '@epoch-ui/components/Badge';
import { LinkButton } from '@epoch-ui/components/LinkButton';
import { EVENTS_LIST_ROUTE } from 'constants/routes';
import { EventType } from 'types/event.types';
import { Tabs, TabsList, TabsTrigger } from 'epoch-ui/components/Tabs/tabs';
import { getEventDateString, getEventTimeString } from 'utils/eventUtils';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';

import { EventStatusChangeDropdown } from './EventStatusChangeDropdown';

interface EventEditorHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const TABS = [
  { path: 'overview', label: 'Overview' },
  { path: 'registration', label: 'Registration' },
  { path: 'communications', label: 'Communications' },
  { path: 'feedback', label: 'Feedback' },
  { path: 'settings', label: 'Settings' }
];

export const EventEditorHeader = (props: EventEditorHeaderProps): React.ReactElement => {
  const event = useEditableEvent();
  const location = useLocation();

  // Dashboard list filters passed via router state (EventsListResults). Captured at
  // mount because navigating between editor tabs clears location.state, and the
  // header stays mounted across tab changes and publish state transitions.
  const [listSearchParams] = useState<string>(() => {
    const params = (location.state as { searchParams?: string } | null)?.searchParams;
    return typeof params === 'string' && params.startsWith('?') ? params : '';
  });

  const currentTab = location.pathname.split('/').pop() || 'overview';

  const isMulti = event.eventType === EventType.MULTI_SESSIONS;
  const eventTypeLabel = isMulti ? 'Multi-session' : 'Single event';

  const dateStr = getEventDateString(event);
  const timeStr = getEventTimeString(event);

  const sessionsQuery = useSessionsQuery(event);

  const timeOrSessionsStr = useMemo(() => {
    if (isMulti) {
      const numOfSessions = sessionsQuery.data?.length ?? 0;
      return numOfSessions > 0 ? `${numOfSessions} session${numOfSessions === 1 ? '' : 's'}` : null;
    }
    return timeStr ?? null;
  }, [isMulti, sessionsQuery.data?.length, timeStr]);

  // Set tab title
  useEffect(() => {
    document.title = `${event.title} | EPOCH`;
  }, [event.title]);

  return (
    <EditorHeader {...props}>
      <EditorHeaderContainer>
        <EditorHeaderTopNav>
          <Link
            to={`${EVENTS_LIST_ROUTE}${listSearchParams}`}
            className="!focus:tw-no-underline tw-text-sm tw-leading-4 tw-text-primary-grey !tw-no-underline hover:tw-text-medium-grey"
          >
            <ArrowLeft size={16} className="tw-mr-1" />
            Dashboard
          </Link>
        </EditorHeaderTopNav>
        <EditorHeaderContent>
          {event.photoUrl ? (
            <EditorHeaderCoverPhoto src={event.photoUrl} alt={event.title || ''} />
          ) : (
            <div
              data-testid="cover-photo-placeholder"
              className="tw-flex tw-aspect-square tw-w-[72px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[16px] tw-bg-background-grey"
            >
              <ImageIcon size={32} className="tw-text-medium-grey" />
            </div>
          )}
          <EditorHeaderInfo className="tw-grow">
            <EditorHeaderTitle>{event.title}</EditorHeaderTitle>
            <div className="tw-flex tw-items-center tw-gap-2">
              <EditorHeaderCaption>
                <span>
                  {dateStr}
                  {timeOrSessionsStr && (dateStr ? ` • ${timeOrSessionsStr}` : timeOrSessionsStr)}
                </span>
                <Badge variant="outline" size="sm" className="tw-ml-1 tw-rounded-[4px]">
                  {eventTypeLabel}
                </Badge>
              </EditorHeaderCaption>
            </div>
          </EditorHeaderInfo>
          <div className="tw-flex tw-gap-2">
            <LinkButton asChild variant="outline" size="sm" className="tw-rounded-[4px]">
              <Link to={`/event/${event.id}`} target="_blank" rel="noopener noreferrer">
                See page
                <MoveUpRightIcon size={16} className="tw-ml-1" />
              </Link>
            </LinkButton>

            <EventStatusChangeDropdown />
          </div>
        </EditorHeaderContent>
        <EditorHeaderTabNav>
          <Tabs value={currentTab}>
            <TabsList className="tw-mb-0">
              {TABS.map((tab) => (
                <TabsTrigger
                  asChild
                  key={tab.path}
                  value={tab.path}
                  className="tw-text-dark-black hover:tw-no-underline"
                >
                  <Link to={tab.path}>{tab.label}</Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </EditorHeaderTabNav>
      </EditorHeaderContainer>
    </EditorHeader>
  );
};
